/**
 * In-memory cache of in-progress intake state, keyed by '<tab>.<field>'.
 *
 * Lets a PM switch screens mid-request (form ↔ PDF ↔ chat, or off to the
 * calendar and back) without losing their form fields, conversation, draft, or
 * uploads. It's deliberately in-memory: it survives client-side navigation for
 * the session but resets on a full page reload — which is fine, since the state
 * includes File objects that can't be serialized to storage anyway.
 *
 * `usePersistedState` is a drop-in replacement for useState that mirrors to the
 * cache and restores from it on remount.
 */
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

const cache = new Map<string, unknown>();

/** useState that mirrors to the session cache and restores on remount. */
export function usePersistedState<T>(
  key: string,
  initial: T,
): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(() =>
    cache.has(key) ? (cache.get(key) as T) : initial,
  );
  useEffect(() => {
    cache.set(key, state);
  }, [key, state]);
  return [state, setState];
}

/** Read a cached value without subscribing (e.g. for RHF defaultValues). */
export function getPersisted<T>(key: string): T | undefined {
  return cache.has(key) ? (cache.get(key) as T) : undefined;
}

/** Write a cached value directly (e.g. from an RHF watch subscription). */
export function setPersisted<T>(key: string, value: T): void {
  cache.set(key, value);
}

/** Drop every cached key for a tab — call after a successful create. */
export function clearIntakeSession(prefix: string): void {
  for (const key of [...cache.keys()]) {
    if (key === prefix || key.startsWith(`${prefix}.`)) cache.delete(key);
  }
}
